import { useState } from "react";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import styles from "./toolbox-gallery.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { ToolboxComponentVisibilityToggle as VisibilityToggle } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-visibility-toggle";
import { ToolboxComponentCheckbox as Checkbox } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-checkbox";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import {
  GalleryPositionIcon,
  GalleryPosition,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/gallery-position-icon";
import { v4 as uuid } from "uuid";
import {
  defaultGallery,
  defaultGalleryCell,
  defaultViewport,
} from "PosterEditor/model/default-project-style";
import { Gallery, GalleryCell, Viewport, pickerColors } from "PosterEditor/model/poster-project";

const GALLERY_GAP_MIN = 0;
const GALLERY_GAP_MAX = 50;
const GALLERY_SIZE_MIN = 10;
const GALLERY_SIZE_MAX = 90;
const GALLERY_SIZE_STEP = 5;
const GALLERY_OPACITY_MIN = 0;
const GALLERY_OPACITY_MAX = 100;

/**
 * Creates a new gallery with default settings and a single viewport cell.
 */
const createDefaultGallery = (): Gallery => {
  const viewportId = uuid();
  const cellId = uuid();
  const galleryId = uuid();

  const newViewport: Viewport = {
    ...defaultViewport,
    id: viewportId,
  };

  const newCell: GalleryCell = {
    ...defaultGalleryCell,
    id: cellId,
    items: [{ type: "viewport", viewport: newViewport }],
  };

  return {
    ...defaultGallery,
    id: galleryId,
    position: { x: 0, y: 0 },
    size: { width: 1.0, height: 0.5 },
    content: [newCell],
  };
};

/**
 * Clamps a gap value to the valid range.
 */
const clampGapValue = (gap: number): number => {
  return Math.max(GALLERY_GAP_MIN, Math.min(GALLERY_GAP_MAX, gap));
};

/**
 * Clamps a size percentage value to the valid range.
 */
const clampSizeValue = (size: number): number => {
  return Math.max(GALLERY_SIZE_MIN, Math.min(GALLERY_SIZE_MAX, size));
};

/**
 * Determines the current gallery position based on its position and size values.
 * Returns undefined if the gallery is in a custom position (not snapped to an edge).
 */
const getGalleryPosition = (gallery: Gallery): GalleryPosition | undefined => {
  const { position, size } = gallery;
  const tolerance = 0.001;

  // Check if snapped to top edge (full width, positioned at top)
  if (
    Math.abs(position.x) < tolerance &&
    Math.abs(position.y) < tolerance &&
    Math.abs(size.width - 1.0) < tolerance
  ) {
    return "top";
  }

  // Check if snapped to bottom edge (full width, positioned at bottom)
  if (
    Math.abs(position.x) < tolerance &&
    Math.abs(position.y - (1 - size.height)) < tolerance &&
    Math.abs(size.width - 1.0) < tolerance
  ) {
    return "bottom";
  }

  // Check if snapped to left edge (full height, positioned at left)
  if (
    Math.abs(position.x) < tolerance &&
    Math.abs(position.y) < tolerance &&
    Math.abs(size.height - 1.0) < tolerance
  ) {
    return "left";
  }

  // Check if snapped to right edge (full height, positioned at right)
  if (
    Math.abs(position.x - (1 - size.width)) < tolerance &&
    Math.abs(position.y) < tolerance &&
    Math.abs(size.height - 1.0) < tolerance
  ) {
    return "right";
  }

  return undefined;
};

/**
 * Gets the size value (as percentage 0-100) for the current gallery position.
 * For horizontal positions (top/bottom), returns height.
 * For vertical positions (left/right), returns width.
 */
const getGallerySizeForPosition = (
  gallery: Gallery,
  position: GalleryPosition | undefined
): number => {
  if (position === "top" || position === "bottom") {
    return Math.round(gallery.size.height * 100);
  } else if (position === "left" || position === "right") {
    return Math.round(gallery.size.width * 100);
  }
  // For custom positions, return the larger dimension as percentage
  return Math.round(Math.max(gallery.size.width, gallery.size.height) * 100);
};

export const ToolboxGallery = () => {
  const { getGallery, setGallery, isTemplateEditing } = usePosterProject();
  const [isColorPickerExpanded, setIsColorPickerExpanded] = useState(false);

  const gallery = getGallery();
  const hasGallery = gallery !== undefined;
  const isVisible = gallery?.isVisible ?? false;
  const currentPosition = gallery ? getGalleryPosition(gallery) : undefined;
  const currentSize = gallery
    ? getGallerySizeForPosition(gallery, currentPosition)
    : 50;

  const handleToggleVisibility = () => {
    if (!hasGallery) {
      // No gallery exists yet, create a new one (visible by default)
      setGallery(createDefaultGallery());
    } else {
      // Gallery exists, toggle its visibility
      setGallery({
        ...gallery,
        isVisible: !gallery.isVisible,
      });
    }
  };

  const handleGapChange = (gap: number) => {
    if (gallery) {
      setGallery({
        ...gallery,
        gap: clampGapValue(gap),
      });
    }
  };

  const handlePositionChange = (position: GalleryPosition) => {
    if (!gallery) return;

    // Preserve the current size ratio when changing position
    const currentSizeRatio =
      currentPosition === "top" || currentPosition === "bottom"
        ? gallery.size.height
        : currentPosition === "left" || currentPosition === "right"
          ? gallery.size.width
          : 0.5; // Default for custom positions

    let newPosition = { x: 0, y: 0 };
    let newSize = { width: 1.0, height: 0.5 };

    switch (position) {
      case "top":
        newPosition = { x: 0, y: 0 };
        newSize = { width: 1.0, height: currentSizeRatio };
        break;
      case "bottom":
        newPosition = { x: 0, y: 1 - currentSizeRatio };
        newSize = { width: 1.0, height: currentSizeRatio };
        break;
      case "left":
        newPosition = { x: 0, y: 0 };
        newSize = { width: currentSizeRatio, height: 1.0 };
        break;
      case "right":
        newPosition = { x: 1 - currentSizeRatio, y: 0 };
        newSize = { width: currentSizeRatio, height: 1.0 };
        break;
    }

    setGallery({
      ...gallery,
      position: newPosition,
      size: newSize,
    });
  };

  const handleSizeChange = (sizePercent: number) => {
    if (!gallery || !currentPosition) return;

    const clampedSize = clampSizeValue(sizePercent);
    const sizeRatio = clampedSize / 100;

    let newPosition = gallery.position;
    let newSize = gallery.size;

    switch (currentPosition) {
      case "top":
        newPosition = { x: 0, y: 0 };
        newSize = { width: 1.0, height: sizeRatio };
        break;
      case "bottom":
        newPosition = { x: 0, y: 1 - sizeRatio };
        newSize = { width: 1.0, height: sizeRatio };
        break;
      case "left":
        newPosition = { x: 0, y: 0 };
        newSize = { width: sizeRatio, height: 1.0 };
        break;
      case "right":
        newPosition = { x: 1 - sizeRatio, y: 0 };
        newSize = { width: sizeRatio, height: 1.0 };
        break;
    }

    setGallery({
      ...gallery,
      position: newPosition,
      size: newSize,
    });
  };

  const handleGapAroundEdgesChange = () => {
    if (gallery) {
      setGallery({
        ...gallery,
        gapAroundEdges: !gallery.gapAroundEdges,
      });
    }
  };

  const handleGapTowardMapChange = () => {
    if (gallery) {
      setGallery({
        ...gallery,
        gapTowardMap: !gallery.gapTowardMap,
      });
    }
  };

  const handleBackgroundColorChange = (color: string) => {
    if (gallery) {
      setGallery({
        ...gallery,
        backgroundColor: color,
      });
    }
  };

  const handleBackgroundOpacityChange = (opacityPercent: number) => {
    if (gallery) {
      setGallery({
        ...gallery,
        backgroundOpacity: opacityPercent / 100,
      });
    }
  };

  // Show for template editing mode, or for non-admins when a gallery exists
  if (!isTemplateEditing && !hasGallery) {
    return null;
  }

  const positions: GalleryPosition[] = ["top", "bottom", "left", "right"];
  const sizeLabel =
    currentPosition === "top" || currentPosition === "bottom"
      ? "Height"
      : currentPosition === "left" || currentPosition === "right"
        ? "Width"
        : "Size";

  return (
    <div className={styles.container}>
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          {/* Visibility section - only for template editing */}
          {isTemplateEditing && (
            <Section title="Visibility">
              <VisibilityToggle
                text="Show gallery on poster"
                isVisible={isVisible}
                onToggle={handleToggleVisibility}
              />
            </Section>
          )}

          {isVisible && gallery && (
            <>
              {/* Position section - only for template editing */}
              {isTemplateEditing && (
                <>
                  <Divider />
                  <Section title="Position">
                    <div className={styles.positionIconsContainer}>
                      {positions.map((position) => (
                        <GalleryPositionIcon
                          key={position}
                          position={position}
                          isSelected={currentPosition === position}
                          onClick={() => handlePositionChange(position)}
                        />
                      ))}
                    </div>
                  </Section>
                </>
              )}

              {/* Size section - only for template editing */}
              {isTemplateEditing && (
                <>
                  <Divider />
                  <Section title="Size">
                    <Slider
                      title={sizeLabel}
                      min={GALLERY_SIZE_MIN}
                      max={GALLERY_SIZE_MAX}
                      value={clampSizeValue(currentSize)}
                      onChange={handleSizeChange}
                      meta={`${clampSizeValue(currentSize)}%`}
                      disabled={!currentPosition}
                    />
                    {!currentPosition && (
                      <p className={styles.helperText}>
                        Select a position to adjust size
                      </p>
                    )}
                  </Section>
                </>
              )}

              {/* Spacing section - available to all users */}
              {isTemplateEditing && <Divider />}
              <Section title="Spacing">
                <Slider
                  title="Gap between items"
                  min={GALLERY_GAP_MIN}
                  max={GALLERY_GAP_MAX}
                  value={clampGapValue(gallery.gap)}
                  onChange={handleGapChange}
                />
                <div className={styles.checkboxGroup}>
                  <Checkbox
                    text="Add gap around gallery edges"
                    isSelected={gallery.gapAroundEdges}
                    onClick={handleGapAroundEdgesChange}
                  />
                  <Checkbox
                    text="Add gap toward map area"
                    isSelected={gallery.gapTowardMap}
                    onClick={handleGapTowardMapChange}
                  />
                </div>
              </Section>

              <Divider />
              {/* Background section - available to all users */}
              <Section title="Background">
                <ColorPicker
                  title="Color"
                  isExpanded={isColorPickerExpanded}
                  colors={pickerColors}
                  showSwatch={true}
                  selectedColor={gallery.backgroundColor}
                  onSelectColor={handleBackgroundColorChange}
                  onToggleExpand={() => setIsColorPickerExpanded(!isColorPickerExpanded)}
                />
                <Slider
                  title="Opacity"
                  min={GALLERY_OPACITY_MIN}
                  max={GALLERY_OPACITY_MAX}
                  value={Math.round(gallery.backgroundOpacity * 100)}
                  onChange={handleBackgroundOpacityChange}
                  meta={`${Math.round(gallery.backgroundOpacity * 100)}%`}
                />
              </Section>
            </>
          )}
        </div>
      </Toolbox>
    </div>
  );
};
